import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StringService } from '@portal-core/general/services/string.service';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { PublicationProfileDialogComponent } from '@portal-core/publications/components/publication-profile-dialog/publication-profile-dialog.component';
import { Publication } from '@portal-core/publications/models/publication.model';
import { PublicationsService } from '@portal-core/publications/services/publications.service';
import { AvatarBase } from '@portal-core/ui/avatar/util/avatar.base';

@Component({
  selector: 'mc-publication-avatar',
  templateUrl: './publication-avatar.component.html',
  styleUrls: ['./publication-avatar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublicationAvatarComponent extends AvatarBase {
  @Input() publication?: Publication;
  @Input() showAllInfo?: boolean = false;

  get name(): string {
    let name = '';

    if (this.publication) {
      name = `${this.publication.Id}`;
    }

    const endedOn = this.publication?.Process?.LastProcessAttempt?.EndedOn;
    if (this.showAllInfo && name && endedOn) {
      const statusDisplayText = this.publicationsService.getStatusDisplayText(this.publication);
      name += ` - ${this.stringService.formatDate(endedOn, 'MMM dd, yyyy h:mm a')} - ${statusDisplayText}`;
    }

    return name;
  }

  get tooltip(): string {
    let tooltip = '';

    if (this.publication) {
      tooltip = `Publication ${this.publication.Id}`;

      const attempt = this.publication.Process?.LastProcessAttempt;
      const endTime = attempt?.EndedOn;
      switch (attempt?.Status) {
        case ProcessAttemptStatus.Complete:
          if (endTime) {
            tooltip += ` completed ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
          }
          break;
        case ProcessAttemptStatus.Running:
          if (endTime) {
            tooltip += ` publishing ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
          }
          break;
        case ProcessAttemptStatus.Failed:
        case ProcessAttemptStatus.Aborted:
          if (endTime) {
            tooltip += ` failed ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
          }
          break;
        case ProcessAttemptStatus.Canceled:
          if (endTime) {
            tooltip += ` canceled ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
          }
          break;
        default:
          tooltip += ` queued ${this.stringService.formatDate(this.publication.CreatedOn, 'MMM dd, yyyy h:mm a')}`;
          break;
      }
    };

    return tooltip;
  }

  constructor(private dialog: MatDialog, private stringService: StringService, private publicationsService: PublicationsService) {
    super();
  }

  onAvatarClicked() {
    this.openPublicationProfileDialog();
  }

  hasAvatarImage(): boolean {
    return false;
  }

  hasPermission(): boolean {
    return true;
  }

  isDeleted(): boolean {
    return false;
  }

  isEmpty(): boolean {
    return !this.publication;
  }

  private openPublicationProfileDialog() {
    this.dialog.open(PublicationProfileDialogComponent, {
      ...PublicationProfileDialogComponent.DialogConfig,
      data: {
        publicationId: this.publication.Id,
      }
    });
  }
}
